import {
  BreakPointCondition,
  Button,
  Icon,
  IconStatus,
  IconTypes,
  Text,
  useBreakpointIf,
  HStack,
  Stack,
} from '@genial/components';
import { Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Box, Pressable } from 'native-base';
import React, { useCallback } from 'react';

import { SignedInNavigationScreenNavigationProp } from '../../../../../routes/types';

namespace HeaderTypes {
  interface StatusType {
    status?: {
      statusNameAlias?: string;
      statusName?: string;
      statusColor?: string;
      statusBackgroundColor?: string;
      statusSize?: string;
      statusIcon?: string;
    };
    statusNameOptout?: string;
    customerId?: number;
    optoutId?: string;
  }

  interface OnPressType {
    onPress: () => void;
  }

  export type UnlockButtonProps = OnPressType & {
    statusNameOptout: string;
  };
  export type GoBackButtonProps = OnPressType;
  export type Props = StatusType;
  export type DetailStatusProps = StatusType;
}

const UnlockButton: React.FC<HeaderTypes.UnlockButtonProps> = ({ onPress, statusNameOptout }) => (
  <Button
    variant={'textLink'}
    onPress={onPress}
    isDisabled={statusNameOptout !== 'Blocked'}
    rightIcon={
      <Icon
        name="Unlock"
        size="xxs"
        testId="unlock-icon"
        color={Color['brand.tertiary.principal']}
      />
    }>
    <Text
      variant="semibold"
      fontSize={Typography['fontsize.md']}
      color={'brand.tertiary.principal'}>
      DESBLOQUEAR
    </Text>
  </Button>
);

const DetailStatus: React.FC<HeaderTypes.DetailStatusProps> = ({ status }) => (
  <HStack alignItems="center" space={Spacing['inline.quarck']}>
    {status?.statusBackgroundColor &&
      status?.statusSize &&
      status?.statusColor &&
      status?.statusIcon && (
        <IconStatus
          testId="status-icon"
          bgColor={status?.statusBackgroundColor as ColorTypes.Option}
          size={status?.statusSize as IconTypes.Props['size']}
          color={status?.statusColor as ColorTypes.Option}
          icon={status?.statusIcon as IconTypes.Props['name']}
        />
      )}
    <Text
      variant="regular"
      color="neutral.black"
      textTransform="uppercase"
      fontSize={Typography['fontsize.xs']}>
      {status?.statusNameAlias ? status?.statusNameAlias : status?.statusName}
    </Text>
  </HStack>
);

const GoBackButton: React.FC<HeaderTypes.GoBackButtonProps> = ({ onPress }) => (
  <Pressable onPress={onPress}>
    <HStack space={Spacing['inline.nano']} alignItems={'center'}>
      <Icon name="ArrowLeft" size="xs" testId="back-icon" />
      <Text variant="semibold" fontSize={Typography['fontsize.lg']}>
        Detalhes do cliente
      </Text>
    </HStack>
  </Pressable>
);

export const Header: React.FC<HeaderTypes.Props> = ({
  status,
  customerId,
  optoutId,
  statusNameOptout,
}) => {
  const navigation = useNavigation();
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  if (isMobile) {
    return (
      <Stack direction={'column'} testID="heading" display="flex" space={Spacing['inline.xxxs']}>
        <GoBackButton onPress={goBack} />
        <HStack alignItems="center" justifyContent="space-between">
          <DetailStatus status={status} customerId={customerId} />
          <UnlockButton
            onPress={() => {
              goBack();
              navigate('UnlockCustomer', {
                customerId: customerId ? customerId?.toString() : '',
                optoutId: optoutId!,
              });
            }}
            statusNameOptout={statusNameOptout ? statusNameOptout : ''}
          />
        </HStack>
      </Stack>
    );
  }

  return (
    <Box
      testID="heading"
      display="flex"
      alignItems="center"
      borderBottomWidth={'0.5px'}
      borderBottomColor={Color['neutral.light']}
      pb={Spacing['inline.xxxs']}>
      <HStack flex={1} alignItems="center" justifyContent={'space-between'} w={'full'}>
        <HStack space={Spacing['inline.nano']} alignItems={'center'} justifyContent={'center'}>
          <GoBackButton onPress={goBack} />
          <DetailStatus status={status} customerId={customerId} />
        </HStack>
        <HStack alignItems={'center'} justifyContent={'center'} space={Spacing['inline.xxs']}>
          <UnlockButton
            onPress={() => {
              goBack();
              navigate('UnlockCustomer', {
                customerId: customerId ? customerId?.toString() : '',
                optoutId: optoutId!,
              });
            }}
            statusNameOptout={statusNameOptout ? statusNameOptout : ''}
          />
          <Pressable onPress={goBack}>
            <Icon name="CloseModal" size="xs" testId="close-icon" variant="rounded-border" />
          </Pressable>
        </HStack>
      </HStack>
    </Box>
  );
};
